import { useState } from 'react'
import { useForm, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { toast } from 'sonner'
import { Leaf } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { useAuth } from '@/contexts/AuthContext'
import { apiUpdateOrgProfile } from '@/lib/api'

const schema = z.object({
  name: z.string().min(1, "Le nom de l'entreprise est requis"),
  logoUrl: z.string().url('URL invalide').or(z.literal('')),
  senderEmail: z.string().email("Format d'email invalide").or(z.literal('')),
})

type FormValues = z.infer<typeof schema>

export type OrganizationSetupProps = {
  onComplete: () => void
}

// Voir note dans Register.tsx — `Input` shadcn n'est pas forwardRef, donc on
// passe par `Controller` pour piloter l'input en mode contrôlé.
export function OrganizationSetup({ onComplete }: OrganizationSetupProps) {
  const { organization } = useAuth()
  const [submitting, setSubmitting] = useState(false)

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<FormValues>({
    mode: 'onBlur',
    resolver: zodResolver(schema),
    defaultValues: {
      name: organization?.name ?? '',
      logoUrl: '',
      senderEmail: '',
    },
  })

  const onSubmit = async (data: FormValues) => {
    setSubmitting(true)
    try {
      await apiUpdateOrgProfile({
        name: data.name.trim(),
        logoUrl: data.logoUrl || undefined,
        senderEmail: data.senderEmail || undefined,
      })
      toast.success('Organisation configurée', { duration: 4000 })
      onComplete()
    } catch {
      toast.error('Erreur lors de la configuration')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="max-w-md mx-auto w-full">
      <div className="flex items-center justify-center mb-6">
        <div className="flex items-center space-x-2">
          <div className="flex items-center justify-center w-10 h-10 rounded-full bg-gradient-to-br from-green-400 to-blue-500 shadow-sm">
            <Leaf className="h-5 w-5 text-white" />
          </div>
          <div className="flex flex-col">
            <span className="font-bold text-green-700 text-lg leading-tight">SustainTrack</span>
            <span className="text-xs text-green-600 leading-tight">Configurer votre organisation</span>
          </div>
        </div>
      </div>

      <form
        onSubmit={handleSubmit(onSubmit)}
        className="space-y-4 bg-white/70 backdrop-blur-sm p-6 rounded-lg border shadow"
      >
        <div>
          <Label htmlFor="setup-name">Nom de l'entreprise *</Label>
          <Controller
            name="name"
            control={control}
            render={({ field }) => (
              <Input
                id="setup-name"
                placeholder="Ma société"
                value={field.value}
                onChange={field.onChange}
                onBlur={field.onBlur}
              />
            )}
          />
          {errors.name && (
            <p className="text-sm text-red-600 mt-1">{errors.name.message}</p>
          )}
        </div>

        <div>
          <Label htmlFor="setup-logo">Logo (URL, optionnel)</Label>
          <Controller
            name="logoUrl"
            control={control}
            render={({ field }) => (
              <Input
                id="setup-logo"
                placeholder="https://…"
                value={field.value}
                onChange={field.onChange}
                onBlur={field.onBlur}
              />
            )}
          />
          {errors.logoUrl && (
            <p className="text-sm text-red-600 mt-1">{errors.logoUrl.message}</p>
          )}
        </div>

        <div>
          <Label htmlFor="setup-sender">Email expéditeur (optionnel)</Label>
          <Controller
            name="senderEmail"
            control={control}
            render={({ field }) => (
              <Input
                id="setup-sender"
                type="email"
                placeholder="noreply@entreprise.com"
                value={field.value}
                onChange={field.onChange}
                onBlur={field.onBlur}
              />
            )}
          />
          {errors.senderEmail && (
            <p className="text-sm text-red-600 mt-1">{errors.senderEmail.message}</p>
          )}
          <p className="text-xs text-muted-foreground mt-1">
            Utilisé comme expéditeur des invitations et relances fournisseurs.
          </p>
        </div>

        <div className="flex flex-col gap-2">
          <Button
            type="submit"
            className="w-full bg-green-600 hover:bg-green-700"
            disabled={submitting}
          >
            {submitting ? 'Enregistrement…' : 'Terminer la configuration'}
          </Button>
          <Button
            type="button"
            variant="ghost"
            onClick={onComplete}
            disabled={submitting}
            className="w-full"
          >
            Passer cette étape
          </Button>
        </div>
      </form>
    </div>
  )
}
